import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import {
    AuthService,
    AuthenticationActions,
    AuthenticationWeatherApiTokenStore,
} from '@navfleet/client/authentication/domain/data-access';
import { LoginResponse } from '@navfleet/client/authentication/domain/models';
import { AppActions } from '@navfleet/client/core/app';
import { LicenseFacade } from '@navfleet/client/license/domain/data-access';
import { MapEncFacade } from '@navfleet/client/map/domain/data-access';
import { ARC_GIS_MAP_TOKEN, DATA_HUB_TOKEN, NAUTICAL_CHART_TOKEN } from '@navfleet/client/map/domain/models';
import { PermissionFacade } from '@navfleet/client/permission/domain/data-access';
import { ErrorWithTranslationCode } from '@navfleet/client/shared/models/common';
import { TenantActions, TenantFacade } from '@navfleet/client/tenant/domain/data-access';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';

@Injectable()
export class AuthenticationEffects {
    private readonly actions$ = inject(Actions);
    private readonly arcGisMapToken = inject<{ token: string }>(ARC_GIS_MAP_TOKEN);
    private readonly authService = inject(AuthService);
    private readonly dataHubToken = inject<{ token: string }>(DATA_HUB_TOKEN);
    private readonly mapEncFacade = inject(MapEncFacade);
    private readonly nauticalChartToken = inject<{ token: string }>(NAUTICAL_CHART_TOKEN);
    private readonly router = inject(Router);
    private readonly tenantFacade = inject(TenantFacade);
    private readonly licenseFacade = inject(LicenseFacade);
    private readonly permissionFacade = inject(PermissionFacade);
    private readonly authenticationWeatherApiTokenStore = inject(AuthenticationWeatherApiTokenStore);

    public readonly initAutoLogin$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthenticationActions.initAutoLogin),
            switchMap(() => this.tenantFacade.getSelectedTenant(true).pipe(take(1))),
            switchMap((tenant) =>
                !tenant
                    ? // If tenant isn't selected the user still might be authenticated
                      // The auth guard will redirect to the login page
                      // Where the user have to select the tenant
                      this.authService
                          // The user might be authenticated but we don't keep the credentials
                          // The browser will provide a secret cookie with token to the server
                          // The cookie is inaccessible from code to prevent hackers from hijacking the user
                          .login({ userName: '', password: '' })
                          .pipe(
                              take(1),
                              mergeMap((response) => this.checkTenantFromResponse(response)),
                              // * Don't catch errors since this is not trigged by the user
                              // If the server returns an error then the user just have to login manually
                              // without being bothered with error messages
                              catchError(() => [
                                  AuthenticationActions.loginFailure({
                                      error: new Error(),
                                  }),
                              ])
                          )
                    : this.authService.login({ tenantId: tenant.id }).pipe(
                          take(1),
                          map((response) => AuthenticationActions.loginSuccess({ response })),
                          catchError(() =>
                              // Clears the selected tenant since the user isn't authenticated
                              // Or have lost the access to the tenant
                              [
                                  AuthenticationActions.loginFailure({
                                      error: new ErrorWithTranslationCode({
                                          message: 'Authentication expired!',
                                          translationCode: 'AUTHENTICATION.EXPIRED',
                                      }),
                                  }),
                                  TenantActions.clearSelectedTenant(),
                              ]
                          )
                      )
            )
        )
    );

    public readonly loginTenant$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthenticationActions.loginTenant),
            mergeMap(({ returnUrl, tenantId }) =>
                this.authService.login({ tenantId }).pipe(
                    take(1),
                    map((response) =>
                        AuthenticationActions.loginSuccess({
                            response,
                            returnUrl,
                        })
                    ),
                    catchError((error) => of(AuthenticationActions.loginFailure({ error })))
                )
            )
        )
    );

    public readonly loginSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthenticationActions.loginSuccess),
            tap(({ returnUrl, response }) => {
                if (returnUrl) {
                    this.router.navigateByUrl(returnUrl);
                }

                if (response?.arcGisMapToken) {
                    this.arcGisMapToken.token = response.arcGisMapToken;
                }

                if (response?.dataHubToken) {
                    this.dataHubToken.token = response.dataHubToken;
                }

                if (response?.encToken) {
                    this.mapEncFacade.setToken(response.encToken);
                }

                if (response?.navChartToken) {
                    this.nauticalChartToken.token = response?.navChartToken;
                }
            }),
            tap(({ response }) => {
                this.licenseFacade.licensesLoaded(response.licenses);
                this.permissionFacade.permissionsLoaded(response.userPermissions);
                this.authenticationWeatherApiTokenStore.setToken(response.weatherToken);
            }),
            mergeMap(({ response }) => [
                TenantActions.tenantsLoaded({
                    tenants: response.tenants,
                }),
                AppActions.loadAppData(),
            ])
        )
    );

    public readonly logout$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(AuthenticationActions.logout),
                tap(() => this.authService.logout())
            ),
        { dispatch: false }
    );

    /**
     * Checks if there are more than one tenant in the response.
     * If there are only one tenant then the login is completed.
     * The correct actions are returned based on the login response.
     * @param response The response from the login.
     * @param returnUrl The url to return to if login success.
     * @returns An array of actions to dispatch.
     */
    private checkTenantFromResponse(response: LoginResponse, returnUrl?: string): Action<any>[] {
        return response.tenants?.length === 1
            ? // If only one tenant then server have returned all of the data needed for login
              [
                  TenantActions.selectedTenantUpdated({
                      tenant: response.tenants[0],
                  }),
                  AuthenticationActions.loginSuccess({
                      response,
                      returnUrl,
                  }),
              ]
            : // More than one tenant. The user have to select tenant.
              [
                  TenantActions.tenantsLoaded({
                      tenants: response.tenants,
                  }),
                  AuthenticationActions.authenticateSuccess({ response }),
              ];
    }
}
